import Highcharts from 'highcharts';

import {Asset} from '@deviceinsight/ng-ui-api-client';

import {ChartSettings, DateCurve, SingleValueChartData} from '../common/types';
import {KpiSinglePeriodChartWidgetConfigSettings} from './types';
import {ChartSeriesType} from '../common/kpiUtils';

const digestSettings = (unit: string): ChartSettings => ({
	xAxis: {
		visible: false
	},
	yAxis: {
		min: 0,
		title: {
			enabled: false
		},
		labels: {
			formatter: function () {
				return `${this.value} ${unit || ''}`;
			}
		}
	}
});

const createSeries = (
	chartDataItem: SingleValueChartData,
	seriesIndex: number,
	showAssetNames: boolean,
	stacked: boolean,
	assets: Asset[]
): DateCurve => {
	const {label, kpiName, color, unit, value, uuid, assetUrn} = chartDataItem;
	const seriesName =
		label || (showAssetNames ? `${assets.find(({urn}) => urn === assetUrn)?.name || '--'} - ${kpiName}` : kpiName);
	return {
		name: seriesName,
		// @ts-ignore
		id: uuid ? uuid : seriesName,
		type: ChartSeriesType.COLUMN,
		color,
		unit,
		data: [value],
		stack: stacked ? 'main' : `stack_${seriesIndex}`
	};
};

export const updateChart = (
	chart: Highcharts.Chart,
	chartDataSets: SingleValueChartData[],
	showAssetNames: boolean,
	settings: KpiSinglePeriodChartWidgetConfigSettings,
	assets: Asset[]
) => {
	if (chart && chart.series) {
		chartDataSets.forEach((dataSet, index) => {
			const {label, assetUrn, kpiName, value, uuid} = dataSet;
			const seriesName =
				label ||
				(showAssetNames ? `${assets.find(({urn}) => urn === assetUrn)?.name || '--'} - ${kpiName}` : kpiName);
			const seriesToUpdate = chart.series.find(({name, userOptions: {id}}) =>
				id && uuid ? id === uuid : name === seriesName
			);

			if (seriesToUpdate) {
				seriesToUpdate.setData([value == null || isNaN(value) ? 0 : value], false);
			} else {
				// add serie, it's not there yet, which should never happen....
				console.warn('Series added afterwards...');
				chart.addSeries(createSeries(dataSet, index, showAssetNames, settings.stacked, assets), false);
			}
		});
		chart.redraw();
	}
};

// chart options
const buildChartOptions = (
	chartSettings: ChartSettings,
	series: Array<DateCurve>,
	stacked: boolean
): Record<string, any> => ({
	chart: {},
	title: {
		text: undefined
	},
	credits: {
		enabled: false
	},
	xAxis: chartSettings.xAxis,
	yAxis: chartSettings.yAxis,
	plotOptions: {
		series: {
			animation: true,
			stacking: stacked ? 'normal' : undefined
		}
	},
	series,
	tooltip: {
		formatter: function () {
			return `${this.series.name}: ${this.y} ${this.series.options.unit || ''}`;
		}
	},
	accessibility: {
		enabled: false
	}
});

export const createChart = (
	domNode: any,
	chartDataSets: SingleValueChartData[],
	{unit, stacked}: KpiSinglePeriodChartWidgetConfigSettings,
	showAssetNames: boolean,
	assets: Asset[]
): any => {
	const chartSettings = digestSettings(unit);
	const series = chartDataSets.map((chartData, index) =>
		createSeries(chartData, index, showAssetNames, stacked, assets)
	);
	const chartOptions = buildChartOptions(chartSettings, series, stacked);
	return Highcharts.chart(domNode, chartOptions);
};
